import { createContext, useState, useEffect, useCallback, useContext } from "react";
import supabase from "../lib/supabase";
import { useAuth } from "./AuthContext";

const WishlistContext = createContext();

export function WishlistProvider({ children }) {
  const { profile } = useAuth();
  const [wishlistItems, setWishlistItems] = useState([]);  // full rows for display
  const [wishlistIds, setWishlistIds]     = useState(new Set()); // fast lookup

  useEffect(() => {
    if (!profile?.id) {
      setWishlistItems([]);
      setWishlistIds(new Set());
      return;
    }
    supabase
      .from("wishlist")
      .select(
        "id, bouquet_id, bouquet:bouquet_id ( id, bouquet_name, image_url, image_urls, price, size_prices )",
      )
      .eq("customer_id", profile.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        const items = data ?? [];
        setWishlistItems(items);
        setWishlistIds(new Set(items.map((r) => r.bouquet_id)));
      });
  }, [profile?.id]);

  const isWishlisted = useCallback(
    (bouquetId) => wishlistIds.has(bouquetId),
    [wishlistIds],
  );

  // bouquetSnapshot: a minimal { id, bouquet_name, image_url, image_urls, price, size_prices }
  // used for the optimistic item so the Favourites tab shows it immediately.
  const toggleWishlist = useCallback(
    async (bouquetId, bouquetSnapshot) => {
      if (!profile?.id) return;
      const liked = wishlistIds.has(bouquetId);

      if (liked) {
        // Optimistic remove
        setWishlistItems((prev) => prev.filter((i) => i.bouquet_id !== bouquetId));
        setWishlistIds((prev) => {
          const next = new Set(prev);
          next.delete(bouquetId);
          return next;
        });
        await supabase
          .from("wishlist")
          .delete()
          .eq("customer_id", profile.id)
          .eq("bouquet_id", bouquetId);
      } else {
        // Optimistic add
        const temp = {
          id: `temp-${bouquetId}`,
          bouquet_id: bouquetId,
          bouquet: bouquetSnapshot,
        };
        setWishlistItems((prev) => [temp, ...prev]);
        setWishlistIds((prev) => new Set([...prev, bouquetId]));

        const { data } = await supabase
          .from("wishlist")
          .insert({ customer_id: profile.id, bouquet_id: bouquetId })
          .select(
            "id, bouquet_id, bouquet:bouquet_id ( id, bouquet_name, image_url, image_urls, price, size_prices )",
          )
          .single();

        if (data) {
          // Replace temp row with the real DB row
          setWishlistItems((prev) =>
            prev.map((i) => (i.id === temp.id ? data : i)),
          );
        }
      }
    },
    [profile?.id, wishlistIds],
  );

  return (
    <WishlistContext.Provider value={{ wishlistItems, isWishlisted, toggleWishlist }}>
      {children}
    </WishlistContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useWishlist = () => useContext(WishlistContext);
